import { apiFetch } from "@/lib/api/client";

export type RankingServiceFlow = "old" | "new";
export type RankingServiceStrategy = "ltr" | "sdr";
export type ManualBoostMode = "interleaved" | "sequential_manual_first";
export type RollUpField = "globalProductId" | "productGrouping.productWallGroupKey";

export interface RankingOptions {
  rankingServiceFlow: RankingServiceFlow;
  rankingServiceStrategy: RankingServiceStrategy;
  manualBoostMode: ManualBoostMode;
  manualBoostSlotsMaxCount: number;
}

export const DEFAULT_RANKING_OPTIONS: RankingOptions = {
  rankingServiceFlow: "old",
  rankingServiceStrategy: "ltr",
  manualBoostMode: "interleaved",
  manualBoostSlotsMaxCount: 50,
};

export interface CompareColumn {
  version: string;
  ranking?: RankingOptions;
  merchRules?: boolean;
  explain?: boolean;
  rollUpField?: RollUpField;
}

export interface CompareRequest {
  baseUrl: string;
  searchParams: {
    channelId: string;
    language: string;
    marketplace: string;
    searchTerms: string;
    anchor: number;
    count: number;
    merchRules: boolean;
    explain: boolean;
    rollUpField?: RollUpField;
    /** Kingpin preview clock for reproducible ranking (ISO-8601 UTC). */
    previewDate?: string;
  };
  headers: {
    accept?: string;
    nikeApiCallerId?: string;
    userAgent?: string;
  };
  /** 1–4 columns; each slot has its own Kingpin version and options. */
  versions: CompareColumn[];
}

export interface CompareColumnResult {
  index: number;
  version: string;
  requestUrl: string;
  httpStatus?: number;
  latencyMs: number;
  body?: unknown;
  error?: string;
}

export interface CompareResponse {
  compareId: string;
  requestedAt: string;
  columns: CompareColumnResult[];
}

const COMPARE_TIMEOUT_MS = 90_000;

export async function runCompare(req: CompareRequest): Promise<CompareResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), COMPARE_TIMEOUT_MS);
  try {
    return await apiFetch<CompareResponse>("/compare/v1", {
      method: "POST",
      body: JSON.stringify(req),
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(
        `Compare timed out after ${COMPARE_TIMEOUT_MS / 1000}s (multi-column + explain can be slow). Try fewer columns or explain=false.`,
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
